import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import type { SegmentedControlProps } from './types';
import { cn } from '../../utils/cn';

/**
 * A segmented control for choosing exactly one value out of a small set of options,
 * e.g. "Gemeinsam | Kreditnehmer 1 | Kreditnehmer 2" or "Ja | Nein | keine Angabe".
 *
 * Behaves like a radio group (single selection, arrow-key navigation) but is rendered
 * as a joined group of buttons where the selected segment is highlighted.
 *
 * @param id - **Required.** Unique identifier for the control.
 * @param options - Options rendered as segments (label, value, optional disabled).
 * @param value - Currently selected value, or `null` for no selection.
 * @param onChange - Called with the value of the clicked segment.
 * @param disabled - Disables the whole control.
 * @param ariaLabel - Accessible label for the radio group.
 * @param className - Additional CSS classes for the group container.
 * @param segmentClassName - Additional CSS classes for every segment button.
 * @param error - Error message displayed below the control.
 *
 * @example
 * <SegmentedControl
 *   id="account-holder"
 *   options={[
 *     { label: 'Gemeinsam', value: 'beide' },
 *     { label: 'Kreditnehmer 1', value: 'antragsteller1' },
 *     { label: 'Kreditnehmer 2', value: 'antragsteller2' },
 *   ]}
 *   value={accountHolder}
 *   onChange={setAccountHolder}
 * />
 */
export const SegmentedControl = <TValue extends string>({
    ariaLabel,
    className,
    disabled = false,
    error,
    id,
    onChange,
    options,
    segmentClassName,
    value,
    wide = false,
}: SegmentedControlProps<TValue>) => {
    const isSegmentDisabled = useCallback(
        (optionDisabled?: boolean) => disabled || Boolean(optionDisabled),
        [disabled],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
            const enabledOptions = options.filter((option) => !isSegmentDisabled(option.disabled));
            if (enabledOptions.length === 0) return;

            const currentEnabledIndex = enabledOptions.findIndex(
                (option) => option.value === options[currentIndex].value,
            );

            let newIndex = -1;

            switch (event.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    newIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledOptions.length - 1;
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    newIndex = currentEnabledIndex < enabledOptions.length - 1 ? currentEnabledIndex + 1 : 0;
                    break;
                case 'Home':
                    event.preventDefault();
                    newIndex = 0;
                    break;
                case 'End':
                    event.preventDefault();
                    newIndex = enabledOptions.length - 1;
                    break;
                default:
                    break;
            }

            if (newIndex !== -1) {
                const newOption = enabledOptions[newIndex];
                onChange(newOption.value);
                document.getElementById(`${id}-segment-${newOption.value}`)?.focus();
            }
        },
        [id, isSegmentDisabled, onChange, options],
    );

    return (
        <div className={cn('inline-flex flex-col', className)}>
            <div
                id={id}
                role="radiogroup"
                aria-label={ariaLabel}
                className={cn(
                    'inline-flex items-stretch rounded border border-grey-stroke bg-white p-0.5 gap-0.5',
                    disabled && 'opacity-50',
                )}
            >
                {options.map((option, index) => {
                    const isSelected = value === option.value;
                    const segmentDisabled = isSegmentDisabled(option.disabled);

                    return (
                        <button
                            key={option.value}
                            id={`${id}-segment-${option.value}`}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            disabled={segmentDisabled}
                            tabIndex={isSelected || (value === null && index === 0) ? 0 : -1}
                            onClick={() => onChange(option.value)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            className={cn(
                                'rounded py-1 text-sm whitespace-nowrap transition-colors select-none',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-focus focus-visible:ring-offset-1',
                                wide ? 'min-w-16 px-2' : 'px-2.5',
                                isSelected
                                    ? 'bg-midnight text-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]'
                                    : 'bg-white text-midnight hover:bg-grey-light',
                                segmentDisabled && 'cursor-not-allowed',
                                segmentClassName,
                            )}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
            {error && <span className="mt-1 text-xs text-alert-error">{error}</span>}
        </div>
    );
};
